import { defineStore } from 'pinia'

import { workflowApi } from '../api/WorkflowApi'
import type {
  WorkflowDefinition,
  WorkflowLoadStatus,
  WorkflowScope,
} from '../models/workflow.types'
import { workflowCategories } from '../service/workflowCatalog'

import { ApiError } from '@/shared/http'

interface WorkflowState {
  definitions: WorkflowDefinition[]
  status: WorkflowLoadStatus
  error: ApiError | null
  loadedContext: string | null
  requestVersion: number
}

function contextKey(scope: WorkflowScope): string {
  return `${scope.accountId}:${scope.workspaceId}`
}

export const useWorkflowStore = defineStore('workflow', {
  state: (): WorkflowState => ({
    definitions: [],
    status: 'idle',
    error: null,
    loadedContext: null,
    requestVersion: 0,
  }),
  getters: {
    categories: (state) => workflowCategories(state.definitions),
    getById: (state) => (id: number | string) =>
      state.definitions.find((definition) => String(definition.id) === String(id)) ?? null,
  },
  actions: {
    hydrate(definitions: WorkflowDefinition[], scope?: WorkflowScope): void {
      this.definitions = definitions
      this.status = 'ready'
      this.error = null
      this.loadedContext = scope ? contextKey(scope) : null
    },
    async fetchDefinitions(
      scope: WorkflowScope,
      reload = false,
      signal?: AbortSignal,
    ): Promise<void> {
      const key = contextKey(scope)
      if (!reload && this.status === 'ready' && this.loadedContext === key) return
      const version = ++this.requestVersion
      this.status = 'loading'
      this.error = null
      try {
        const definitions = await workflowApi.listDefinitions(scope, signal)
        if (version !== this.requestVersion) return
        this.definitions = definitions
        this.loadedContext = key
        this.status = 'ready'
      } catch (cause) {
        if (version !== this.requestVersion) return
        this.error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
        this.status = 'error'
      }
    },
    reset(): void {
      this.requestVersion += 1
      this.definitions = []
      this.status = 'idle'
      this.error = null
      this.loadedContext = null
    },
  },
})
